import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { deleteExamPaperQuestion, ExamPaperDetail, ExamPaperQuestionItem, fetchExamPaperDetail, insertExamPaperQuestion } from '@/api/exam';
import { QuestionList } from './components/QuestionList';
import { MetadataForm } from './components/MetadataForm';
import { SingleQuestionEditor } from './components/SingleQuestionEditor';
import { GroupQuestionEditor } from './components/GroupQuestionEditor';
import { cn } from '@/lib/utils';

export default function PaperEditor() {
  const { paperId } = useParams<{ paperId: string }>();
  const navigate = useNavigate();
  const [paper, setPaper] = useState<ExamPaperDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const fetchPaper = async () => {
    if (!paperId) return;
    try {
      setLoading(true);
      const data = await fetchExamPaperDetail(paperId);
      setPaper(data);
    } catch (error: any) {
      toast.error(error.message || '加载试卷失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaper();
  }, [paperId]);

  const handleMetadataUpdate = (updatedPaper: ExamPaperDetail) => {
    setPaper(updatedPaper);
    toast.success('试卷信息已更新');
  };

  const handleQuestionSave = () => {
    fetchPaper(); // Refresh list content
  };

  const handleGroupUpdate = (updatedPaper: ExamPaperDetail) => {
    setPaper(updatedPaper);
    fetchPaper(); // Refresh to get latest data
  };

  const handleSelectQuestion = (questionId: string) => {
    setSelectedQuestionId(questionId);
    setSelectedGroupId(null);
  };

  const handleSelectGroup = (groupId: string) => {
    setSelectedGroupId(groupId);
    setSelectedQuestionId(null);
  };

  const handleBackToMetadata = () => {
    setSelectedQuestionId(null);
    setSelectedGroupId(null);
  };

  const handleInsertQuestionAfter = async (afterQuestionId: string | null) => {
    if (!paperId) return
    try {
      const resp = await insertExamPaperQuestion(paperId, {
        insert_after_question_id: afterQuestionId,
        type: "blank",
      })
      setPaper(resp.paper)
      setSelectedQuestionId(resp.inserted_question_id)
      setSelectedGroupId(null)
      toast.success('已插入新题目')
    } catch (error: any) {
      toast.error(error.message || '插入题目失败')
    }
  }

  const handleDeleteQuestion = async (questionId: string) => {
    if (!paperId) return
    const ok = window.confirm('确认删除该题目？删除后不可恢复。')
    if (!ok) return
    try {
      await deleteExamPaperQuestion(paperId, questionId)
      if (selectedQuestionId === questionId) {
        setSelectedQuestionId(null)
      }
      toast.success('题目已删除')
      fetchPaper()
    } catch (error: any) {
      toast.error(error.message || '删除题目失败')
    }
  }

  // Find group data for the selected group
  const selectedGroupData = selectedGroupId && paper ? findGroupData(paper.questions, selectedGroupId) : null

  const isEditingMetadata = !selectedQuestionId && !selectedGroupId

  if (loading) {
    return <div className="flex items-center justify-center h-screen">加载中...</div>;
  }

  if (!paper) {
    return <div className="flex items-center justify-center h-screen">试卷不存在</div>;
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <header className="h-14 border-b flex items-center px-4 gap-4 bg-card shrink-0">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-lg font-semibold truncate flex-1">{paper.name}</h1>
        <div className="text-sm text-muted-foreground hidden sm:block">
          {selectedGroupId ? '编辑题组' : selectedQuestionId ? '编辑题目' : '编辑试卷信息'}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 sm:w-80 border-r flex flex-col bg-muted/10 shrink-0">
          <div className="p-2 border-b shrink-0">
            <Button
              variant={isEditingMetadata ? "secondary" : "ghost"}
              className={cn("w-full justify-start gap-2", isEditingMetadata && "bg-accent text-accent-foreground")}
              onClick={handleBackToMetadata}
            >
              <Settings className="w-4 h-4" />
              <span>试卷信息</span>
            </Button>
          </div>
          <div className="flex-1 overflow-hidden">
            <QuestionList
              questions={paper.questions}
              selectedQuestionId={selectedQuestionId}
              onSelect={handleSelectQuestion}
              onSelectGroup={handleSelectGroup}
              onInsertAfter={handleInsertQuestionAfter}
              onDelete={handleDeleteQuestion}
            />
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-background p-4 sm:p-8">
          <div className="max-w-3xl mx-auto w-full">
            {selectedGroupData ? (
              <GroupQuestionEditor
                key={selectedGroupId}
                groupId={selectedGroupData.groupId}
                groupType={selectedGroupData.groupType}
                passageContent={selectedGroupData.passageContent}
                groupOptions={selectedGroupData.groupOptions}
                subQuestions={selectedGroupData.subQuestions}
                onUpdate={handleGroupUpdate}
                onCancel={handleBackToMetadata}
              />
            ) : selectedQuestionId ? (
              <SingleQuestionEditor
                key={selectedQuestionId}
                questionId={selectedQuestionId}
                onSave={handleQuestionSave}
              />
            ) : (
              <MetadataForm
                paper={paper}
                onUpdate={handleMetadataUpdate}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

/** Find group data from the flat question list */
function findGroupData(questions: ExamPaperQuestionItem[], groupId: string) {
  const groupQ = questions.find(q => q.group_id === groupId)
  if (!groupQ) return null

  const subQuestions = questions.filter(q => q.group_id === groupId)

  return {
    groupId,
    groupType: groupQ.group_type || 'reading_comp',
    passageContent: groupQ.passage_content || '',
    groupOptions: groupQ.group_options || [],
    subQuestions,
  }
}
